import { Component, DestroyRef, OnInit, output, inject } from '@angular/core';
import { UntypedFormGroup, FormControl, Validators, UntypedFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { Financing, Customer, Proposal, PreferredProposal, NewFinancingBuilding, NewFinancing, Rejection, RejectionBuilding, ProposalBuilding, RejectionBuildingUnit, ProposalBuildingUnit, RejectionBorrower, ProposalBorrower, Address } from '@app/core/stingray-api/models';
import { FinancingsService } from '@app/core/stingray-api/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import _ from 'lodash';
import moment from 'moment';
import { Observable, finalize, distinctUntilChanged, debounceTime, from, concatMap, last } from 'rxjs';
import { ProposalLender } from '../selected-proposal-card/proposal-lender';
import { ProposalView } from '../selected-proposal-card/proposal-view';
import { InterestPeriodInMonthsFilter } from './interest-period-in-monts-filter';
import { InvalidProposal } from './invalid-proposal';
import { RepaymentRateFilter } from './repayment-rate-filter';
import { Options, NgxSliderModule } from '@angular-slider/ngx-slider';
import { AddressRow } from '../address-selection-list/address-row';
import { StarRatingModule } from 'angular-star-rating';
import { LoadingButtonComponent } from '../loading-button/loading-button.component';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelActionRow } from '@angular/material/expansion';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { digitsOnlyDirective } from '../directives/digits-only.directive';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatPrefix } from '@angular/material/form-field';
import { MatCard, MatCardContent } from '@angular/material/card';
import { NgTemplateOutlet, LowerCasePipe, DecimalPipe, CurrencyPipe } from '@angular/common';

@Component({
    selector: 'unite-calculate-financing-proposals',
    templateUrl: './calculate-financing-proposals.component.html',
    styleUrls: ['./calculate-financing-proposals.component.scss'],
    imports: [MatCard, MatCardContent, TranslateModule, NgxSliderModule, ReactiveFormsModule, MatFormField, MatPrefix, MatInput, digitsOnlyDirective, MatSelect, MatOption, MatButton, MatProgressSpinner, MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, NgTemplateOutlet, MatTooltip, MatIcon, MatExpansionPanelActionRow, LoadingButtonComponent, StarRatingModule, LowerCasePipe, DecimalPipe, CurrencyPipe]
})
export class CalculateFinancingProposalsComponent  implements OnInit {
  private destroyRef = inject(DestroyRef);
  completed = output<void>();

  isCalculatingProposals = true;
  isCreatingFinancing = true;
  financing: Financing = null;
  maxLoan: number = null;
  filterFormGroup: UntypedFormGroup;
  selectedAddresses: AddressRow[] = [];

  isSelectingProposals = false;
  selectedProposalView: ProposalView = null;

  // set from outside component
  customer: Customer = null;
  refinancingAmount: number = 0;

  // The sort to use after the max loan has been calculated
  preferredSort = 'AverageTotalCostsPerMonth';
  preferredLoan: number;

  filterInterestPeriodsInMonths: InterestPeriodInMonthsFilter[] = [
    { months: 12, reflection: 0, active: true },
    { months: 24, reflection: 0, active: true },
    { months: 36, reflection: 0, active: true },
    { months: 36, reflection: 24, active: true },
    { months: 60, reflection: 0, active: true },
    { months: 84, reflection: 0, active: true },
    { months: 120, reflection: 0, active: true }
  ];

  filterRepaymentRate: RepaymentRateFilter[] = [
    { value: 0, active: true, label: '0%', type: 'exact' },
    { min: 0, max: 2, active: true, label: '> 0% - 2%', type: 'between' },
    { min: 2, max: 4, active: true, label: '> 2% - 4%', type: 'between' },
    { min: 4, max: 100, active: true, label: '> 4%', type: 'between' }
  ];

  filterLoanToValue = new FormControl(0, { validators: [Validators.required] });
  filterOptionsLoanToValue: Options = {
    floor: 20,
    ceil: 90,
    step: 5,
    enforceStep: false,
    translate: (value: number): string => value.toFixed(0) + '%'
  };

  rejectedProposals: InvalidProposal[] = [];
  allProposals: ProposalView[] = [];
  highestProposals: ProposalView[] = [];

  invalidProposals: InvalidProposal[] = [];
  validProposals: ProposalView[] = [];

  constructor(
    private activatedRoute: ActivatedRoute,
    private financingsService: FinancingsService,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private translateService: TranslateService,
  ) { }

  ngOnInit(): void {
    this.filterFormGroup = this.createFilterFormGroup();

    this.filterLoanToValue.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(x => {
        const currentPreferredLoan = this.filterFormGroup.get('preferredLoan').value;
        const newPreferredLoan = parseInt(((x / 100) * this.totalBuildingValue()).toFixed(0), 10);

        if (currentPreferredLoan !== newPreferredLoan) {
          this.filterFormGroup.get('preferredLoan').setValue(newPreferredLoan, { emitEvent: !this.isCalculatingProposals });
          this.updateRoute();
        }
      });

    this.activatedRoute.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {

        /* Preset preferred sort */
        if (params.sort) {
          this.preferredSort = params.sort;
        }

        /* Preset preferred loan */
        if (params.loan) {
          this.preferredLoan = params.loan;
        }

        /* Load interest period filter */
        if (params.periods) {
          this.filterInterestPeriodsInMonths.forEach(filter => filter.active = false)

          params.periods.split(',').forEach((months: string) => {
            var period = 0;
            var reflection = 0;

            if (months.indexOf('-') > 0) {
              period = parseInt(months.split('-')[0], 10);
              reflection = parseInt(months.split('-')[1], 10);
            }
            else {
              period = parseInt(months, 10);
            }

            this.filterInterestPeriodsInMonths.filter(filter => filter.months === period && filter.reflection == reflection)
              .forEach(filter => filter.active = true);
          });
        }

        /* Load repayment filter */
        if (params.repayments) {
          params.repayments.split(',').forEach((rate: string) => {
            this.filterRepaymentRate.filter(filter => filter.max === parseInt(rate, 10)).forEach(filter => filter.active = true);
          });
        }
      });
  }

  public recalculateProposals(): Observable<boolean> {

    return new Observable<boolean>(obj => {

      this.isCalculatingProposals = true;

      const preferredLoan = Math.round(+this.filterFormGroup.get('preferredLoan').value);

      const ops = [
        { path: 'calculationType', value: 'SingleLoan', op: 'replace' },
        { path: 'preferredLoan', value: preferredLoan, op: 'replace' },
        { path: 'sort', value: this.filterFormGroup.get('sort').value, op: 'replace' },
      ];

      if (this.financing.proposalsFilter) {
        ops.push({ path: 'proposalsFilter/includeInvalid', value: true, op: 'replace' });
      } else {
        ops.push({ path: 'proposalsFilter', value: { includeInvalid: true }, op: 'replace' });
      }

      const ltv = (preferredLoan / this.totalBuildingValue()) * 100;
      this.filterLoanToValue.setValue(ltv, { emitEvent: false });

      this.financingsService
        .patchFinancing({ id: this.financing.id, body: ops })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(patchedFinancing => {
          this.financing = patchedFinancing;
          this.calculateProposals().subscribe(x => {
            obj.next(true);
            obj.complete();
          });
        });

    });
  }
  /**
   * Validates if the preferred proposals on the financing
   * are in the result set that's calculated.
   */
  public arePreferredProposalValid(): boolean {

    const preferredProposals = this.financing.preferredProposals;
    const validCompareA = _.sortBy(preferredProposals.map(x => ({ lenderName: x.lenderName, rateName: x.rateName, loan: x.loan, interestRate: x.interestRate })), x => x.lenderName);

    let foundIdentical = false;
    this.validProposals.forEach(validProposal => {

      const validCompareB = _.sortBy(validProposal.proposals.map(x => ({ lenderName: x.rate.lenderName, rateName: x.rate.name, loan: x.loan, interestRate: x.interestRate })), x => x.lenderName);

      if (_.isEqual(validCompareA, validCompareB)) {
        console.log('Found identical', validCompareA, validCompareB);
        foundIdentical = true;
      }

    });

    return foundIdentical;
  }

  /**
   * Is this proposal filterable (by the active, or by any of the filters)
   */
  isFilteredProposal(proposal: Proposal, onlyActiveFilters: boolean): boolean {
    const filteredPeriods = this.filterInterestPeriodsInMonths.filter(x => x.active || !onlyActiveFilters);
    const filteredRepaymentRates = this.filterRepaymentRate.filter(x => x.active || !onlyActiveFilters);

    return filteredPeriods.filter(p => {
      if (p.reflection > 0) {
        return proposal.interestPeriodInMonths == p.months && (proposal.interestReflectionPeriodInMonths ?? 0) == p.reflection;
      }

      return proposal.interestPeriodInMonths == p.months || proposal.interestPeriodInMonths + (proposal.interestReflectionPeriodInMonths ?? 0) == p.months;
    }).length > 0 &&
      filteredRepaymentRates.filter(f => {
        const repaymentRatePerYear = +proposal.repaymentRatePerYear.toFixed(2);
        switch (f.type) {
          case 'exact': return repaymentRatePerYear === f.value;
          case 'between': return repaymentRatePerYear > f.min && repaymentRatePerYear <= f.max;
        }
      }).length > 0;

  }

  setPreferredLoan(preferredLoan: number): void {

    this.filterFormGroup.get('preferredLoan').setValue(preferredLoan, { emitEvent: false });
    this.preferredLoan = preferredLoan;
    this.recalculateProposals().subscribe(x => {
      this.updateRoute();
    });

  }

  setFilterRepaymentRate(filterRepaymentRate: RepaymentRateFilter, active: boolean): void {
    filterRepaymentRate.active = active;
    this.setInvalidProposals();
    this.setValidProposals();
    this.updateRoute();
  }

  setFilterInterestPeriodsInMonths(filterInterestPeriodsInMonths: InterestPeriodInMonthsFilter, active: boolean): void {
    filterInterestPeriodsInMonths.active = active;

    this.setInvalidProposals();
    this.setValidProposals();
    this.updateRoute();
  }

  /**
   * Returns all addresses on this propsal view
   */
  getProposalAddresses(proposalView: ProposalView): Address[] {
    return _.uniqBy(_.flatten(_.concat(proposalView.proposals.map(x => x.buildings.map(a => a.address)))), x => `${x.addressLine1}, ${x.city}`);
  }

  isAddressFinancedOnProposal(address: Address, proposal: Proposal): boolean {
    return _.filter(proposal.buildings, b => _.isEqual(b.address, address) && b.financingValue > 0).length > 0;
  }

  /**
   * Select a proposal view which will be used to apply for financing
   */
  selectProposals(proposalView: ProposalView): void {

    this.selectedProposalView = proposalView;
    this.isSelectingProposals = true;
    this.financingsService.patchFinancing({
      id: this.financing.id,
      body: [
        {
          path: 'preferredProposals', op: 'replace', value: proposalView.proposals.map(x => <PreferredProposal>{
            contributionOwnResources: x.contributionOwnResources,
            expirationDate: moment(this.financing.executionDate).add(proposalView.interestPeriodInMonths, 'months').locale('en').format('L'),
            interestRate: x.interestRate,
            lenderName: x.rate.lenderName,
            lenderNumber: x.rate.lenderNumber,
            loan: x.loan,
            loanToValue: x.loanToValue,
            rateName: x.rate.name,
            rateNumber: x.rate.number,
            repaymentRatePerYear: x.repaymentRatePerYear,
            startDate: moment(this.financing.executionDate).locale('en').format('L')
          })
        }
      ]
    })
      .pipe(finalize(() => this.isSelectingProposals = false), takeUntilDestroyed(this.destroyRef))
      .subscribe(patchedFinancing => {
        this.financing = patchedFinancing;
        this.completed.emit();
      });
  }



  /**
   * Returns the total building value for all selected buildings
   */
  totalBuildingValue(): number {
    return this.selectedAddresses.reduce((total, a) => total + (a.editBuildingModel.building.value ?? 0), 0);
  }

  public saveFinancing(): void {

    this.allProposals = null;
    this.highestProposals = null;
    this.maxLoan = null;

    this.filterFormGroup = this.createFilterFormGroup();
    this.isCalculatingProposals = false;
    this.isCreatingFinancing = true;

    // Can contain a preloaded financing
    if (this.financing) {
      this.patchFinancing()
        .pipe(finalize(() => this.isCreatingFinancing = false), takeUntilDestroyed(this.destroyRef))
        .subscribe(patchedFinancing => {
          this.financing = patchedFinancing;
          this.updateRoute();
          this.calculateProposals().subscribe(x => {

          });
        });
    } else {
      
      const purchaseDates = this.selectedAddresses
        .map(x => x.editBuildingModel.building.purchaseDate)
        .filter(date => moment(date).isAfter())
        .sort(function(a, b) {
          return Date.parse(a) - Date.parse(b);
        });

      const startDate = purchaseDates.length > 0 ? purchaseDates[0] : moment().add(1, 'month');

      const newFinancing = {
        calculationType: 'All',
        customerId: this.customer.id,
        startDate: (moment(startDate).locale('en').format('L')),
        buildings: this.selectedAddresses.map(x => ({
          id: x.editBuildingModel.building.id,
          startDate: (moment(startDate).locale('en').format('L')),
        } as NewFinancingBuilding)),
        refinancingAmount: this.refinancingAmount
      } as NewFinancing;

      this.financingsService
        .addFinancing({ body: newFinancing })
        .pipe(finalize(() => this.isCreatingFinancing = false), takeUntilDestroyed(this.destroyRef))
        .subscribe(x => {
          this.financing = x[0];

          this.patchFinancing()
            .subscribe(patchedFinancing => {
              this.financing = patchedFinancing;
              this.updateRoute();
              this.calculateProposals().subscribe();
            });
        });
    }
  }

  private createFilterFormGroup(): UntypedFormGroup {
    const form = this.formBuilder.group({
      preferredLoan: [, [Validators.required]],
      sort: ['Loan', []]
    });


    form.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(1500), takeUntilDestroyed(this.destroyRef))
      .subscribe(x => {

        if (form.valid) {
          this.updateRoute();
        }

        if (form.valid && this.financing) {
          this.recalculateProposals().subscribe();
        }
      });


    return form;
  }

  private calculateProposals(): Observable<boolean> {

    return new Observable<boolean>((obj) => {
      this.isCalculatingProposals = true;
      const isMaxLoanCalculation = this.financing.calculationType === 'All';

      this.financingsService.compose({ id: this.financing.id })
        .pipe(finalize(() => this.isCalculatingProposals = isMaxLoanCalculation), takeUntilDestroyed(this.destroyRef)) // Keep it on true, when a preferred loan calculation follows
        .subscribe(proposals => {

          const composedProposals = proposals;
          const calculatedProposals = composedProposals.compositions
            .filter(x => _.every(x.proposals, p => this.isFilteredProposal(p, false))) // Every proposal of a composition should be allowed to made visible
            .map(x => ({
              proposals: x.proposals,
              lenders: x.proposals.map(p => ({ lenderName: p.rate.lenderName, rate: p.rate.name } as ProposalLender)),
              averageTotalCostsPerMonth: x.averageTotalCostsPerMonth,
              commission: x.proposals.filter(p => p.commissionPercentage >= 1).length > 0,
              interestDeposit: x.proposals.filter(p => p.interestDepositAmount > 0).length > 0,
              interestRate: x.interestRate,
              interestPeriodInMonths: x.interestPeriodInMonths,
              interestReflectionPeriodInMonths: _.max(x.proposals.map(p => p.interestReflectionPeriodInMonths)),
              repaymentRatePerYear: x.repaymentRatePerYear,
              monthlyCharges: x.monthlyCharges,
              loan: x.loan,
              maximumLoan: null,
              compensation: _.sum(x.proposals.map(p => p.costsBuyer.compensation)) < 0,
              valid: x.valid,
              composed: x.proposals.length > 1,
              compositionId: composedProposals.compositionId,
              costs: _.sum(x.proposals.map(p => _.sum(p.submitOptions.map(y => y.costs)))),
              calculationId: null,
              score: x.score / 100
            } as ProposalView));

          /* Set the highest loan, upon entry */
          if (isMaxLoanCalculation) {

            const highestLoanProposal = _.orderBy(calculatedProposals.filter(x => x.valid), x => x.loan, 'desc')[0];
            if (!highestLoanProposal) {
              console.warn('No valid loan proposal found', calculatedProposals.filter(x => x.valid));
              this.maxLoan = 0.8 * this.totalBuildingValue();
            } else {
              this.maxLoan = highestLoanProposal.loan;
            }

            this.filterFormGroup.get('preferredLoan').setValue(this.preferredLoan ?? this.maxLoan, { emitEvent: false });
            this.filterFormGroup.get('sort').setValue(this.preferredSort, { emitEvent: true });

            this.highestProposals = calculatedProposals;
          } else {
            this.allProposals = calculatedProposals;

            this.rejectedProposals = composedProposals.rejections.map(x => ({
              lenders: [{ lenderName: x.lenderName, rate: x.rate }],
              descriptions: this.getLimits(x, null)
            } as InvalidProposal));

            this.setInvalidProposals();
            this.setValidProposals();
          }

          obj.next(true);
          obj.complete();
        });
    });
  }

  private setValidProposals(): void {
    this.validProposals = [];

    if (this.allProposals) {
      const filteredProposals = this.allProposals.filter(x => x.valid && this.isFilteredProposal(x, true));
      filteredProposals.forEach(p => {

        /* Set the field maximum loan with a valid precalculated value */
        p.maximumLoan = this.getMaxLoanAmount(p.lenders);

        this.validProposals.push(p);
      });
    }
  }

  private setInvalidProposals(): void {
    this.invalidProposals = [];

    if (this.allProposals) {
      const invalidProposals: InvalidProposal[] = [];

      // Step 1: add the rejected proposals, these are filter ignorant
      this.rejectedProposals.forEach(p => invalidProposals.push(p));

      // Step 2: add the invalid proposals
      this.allProposals.filter(x => !x.valid && this.isFilteredProposal(x, true)).map(p => {

        /* Set the field maximum loan with a valid precalculated value */
        p.maximumLoan = this.getMaxLoanAmount(p.lenders);

        const invalidProposal = {
          lenders: p.lenders,
          descriptions: _.flatten(p.proposals.map(x => this.getLimits(x, p.maximumLoan))),
          // Remove the view for invalid proposals containing the subscription rejection code
          proposalView: p
        } as InvalidProposal;


        return invalidProposal;

      }).forEach(p => invalidProposals.push(p));

      this.invalidProposals = _.orderBy(invalidProposals, x => x.lenders.length === 1 ? `${x.lenders[0].lenderName} ${x.lenders[0].rate}` : '####');
    }
  }

  private getMaxLoanAmount(lenders: ProposalLender[]): number {
    if (!this.highestProposals) {
      return null;
    }

    const maxLoanProposal = _.orderBy(this.highestProposals.filter(x =>
      x.valid &&
      _.isEqual(_.sortBy(x.lenders), _.sortBy(lenders))), x => x.loan, 'desc')[0];

    if (maxLoanProposal) {
      return maxLoanProposal.loan;
    }

    return null;
  }

  /**
   * Flattens the limits on a proposal
   */
  private getLimits(item: Rejection | Proposal, maxLoan: number): string[] {

    const descriptions: string[] = [];

    // Proposal limits
    item.limits.forEach(limit => {
      if (limit && limit.description != null && descriptions.indexOf(limit.description) === -1) {

        if (limit.code === '999') {
          descriptions.push(this.translateService.instant('Not available'));
        } else { //if (limit.code !== '61' || !maxLoan) { // 
          descriptions.push(limit.description);
        }

      }
    });

    item.buildings.forEach((building: RejectionBuilding | ProposalBuilding) => {

      // Building limits
      building.limits.forEach(limit => {
        const description = `${building.address.addressLine1} - ${limit.description}`;

        if (limit && limit.description && descriptions.indexOf(description) === -1) {
          descriptions.push(description);
        }

        // Unit limits
        building.units.forEach((unit: RejectionBuildingUnit | ProposalBuildingUnit) => {
          unit.limits.forEach(unitLimit => {

            const unitDescription = `${building.address.addressLine1} - ${unitLimit.description}`;

            if (unitLimit && unitDescription != null && descriptions.indexOf(unitDescription) === -1) {
              descriptions.push(unitDescription);
            }
          });
        });
      });
    });

    // Borrower limits
    item.borrowers.forEach((borrower: RejectionBorrower | ProposalBorrower) => {

      borrower.limits.forEach(limit => {

        const description = `${(borrower.name != null ? borrower.name + ' - ' : '')}${limit.description}`;

        if (limit && description != null && descriptions.indexOf(description) === -1) {
          descriptions.push(description);
        }
      });

    });

    return descriptions;
  }

  private patchFinancing(): Observable<Financing> {

    return new Observable((obs) => {

      this.updateFinancingBuildings()
        .subscribe(x => {

          // Reset filters
          const patchOperations = [
            { path: 'proposalsDistinction', value: 'LenderAndInterestDuration', op: 'replace' },
            { path: 'sort', value: this.filterFormGroup.get('sort').value, op: 'replace' },
            { path: 'calculationType', value: 'All', op: 'replace' },
            { path: 'refinancingAmount', value: this.refinancingAmount, op: 'replace' }
          ];

          this.financingsService.patchFinancing({
            id: this.financing.id,
            body: patchOperations
          })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(patchedFinancing => {
              obs.next(patchedFinancing);
              obs.complete();
            });
        });
    });
  }

  private updateFinancingBuildings(): Observable<boolean> {
    return new Observable<boolean>((obs) => {
      this.financingsService
        .getFinancingBuildings({ id: this.financing.id })
        .subscribe(buildings => {
          const requests = [];

          const buildingIdsOnFinancing = buildings.map(x => x.id);
          const buildingIdsOnView = this.selectedAddresses.map(x => x.editBuildingModel.building.id);

          const removedBuildings = buildingIdsOnFinancing.filter(x => buildingIdsOnView.indexOf(x) === -1);
          const addedBuildings = buildingIdsOnView.filter(x => buildingIdsOnFinancing.indexOf(x) === -1);


          if (removedBuildings.length > 0) {
            requests.push(...removedBuildings.map(b => this.financingsService.deleteFinancingBuilding({ id: this.financing.id, buildingId: b })));
          }

          if (addedBuildings.length > 0) {
            requests.push(...addedBuildings.map(b => this.financingsService.addFinancingBuilding({
              id: this.financing.id, body:
                {
                  id: b,
                  startDate: (moment().locale('en').format('L')),
                } as NewFinancingBuilding
            })));
          }

          if (requests.length > 0) {
            from(requests).pipe(
              concatMap(updateFinancingBuildingRequests => updateFinancingBuildingRequests)
            )
              .pipe(last(), takeUntilDestroyed(this.destroyRef))
              .subscribe((b) => {
                obs.next(true);
                obs.complete();
              });
          } else {
            obs.next(true);
            obs.complete();
          }

        });
    });
  }

  /**
   * Updates the visual url so it can be refreshed and reload data
   */
  private updateRoute(): void {

    const activePeriods = this.filterInterestPeriodsInMonths.filter(x => x.active);
    const activeRepayments = this.filterRepaymentRate.filter(x => x.active);
    const sort = this.filterFormGroup.get('sort').value !== 'Loan' ? this.filterFormGroup.get('sort').value : null;
    const loan = this.filterFormGroup.get('preferredLoan').value > 0 ? this.filterFormGroup.get('preferredLoan').value : null;

    const queryParams: Params = {
      financingId: this.financing.id,
      periods: activePeriods.length > 0 ? activePeriods.map(a => a.reflection > 0 ? a.months + '-' + a.reflection : a.months).join(',') : null,
      repayments: activeRepayments.length > 0 ? activeRepayments.map(a => a.max).join(',') : null,
      sort: sort,
      loan: loan
    };

    // Having a financing is the bare minimum
    if (queryParams.financingId) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: queryParams,
        queryParamsHandling: 'merge',
      });
    }
  }
}

